import Axios, { AxiosResponse, AxiosError, isAxiosError } from "axios";
import { API } from "../config";
import { getUserFromLocalStorage, logout } from "../_services";

const handleResponse = (response: AxiosResponse) => response.data;

const INVALID_FIELD = "dane nieprawidłowe";

/** node-input-validator uses { message, rule } or arrays of those per field. */
function formatValidationEntry(val: unknown): string {
  if (typeof val === "string") return val;
  if (Array.isArray(val)) {
    const parts = val.map(formatValidationEntry).filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : INVALID_FIELD;
  }
  if (val !== null && typeof val === "object") {
    const o = val as { message?: unknown };
    if (typeof o.message === "string") return o.message;
  }
  return INVALID_FIELD;
}

/** Formats API error for inline UI (replaces generic alerts for callers that catch errors). */
export function formatAxiosErrorMessage(error: unknown): string {
  if (!isAxiosError(error)) {
    if (error instanceof Error) return error.message;
    return "Wystąpił nieoczekiwany błąd.";
  }
  const payload = error.response?.data as { message?: unknown } | undefined;
  if (payload?.message !== undefined) {
    const msg = payload.message;
    if (typeof msg === "string") return msg;
    if (msg !== null && typeof msg === "object") {
      const lines: string[] = [];
      for (const [key, val] of Object.entries(msg)) {
        if (val == null) continue;
        lines.push(`${key}: ${formatValidationEntry(val)}`);
      }
      return lines.length > 0 ? lines.join("\n") : JSON.stringify(msg);
    }
  }
  if (!error.response) {
    return "Problem z połączeniem!";
  }
  return error.message || "Wystąpił błąd serwera.";
}

const rejectAfterAuthHandling = (error: AxiosError<{ message?: unknown }>) => {
  if (!error.response) {
    throw new Error("Problem z połączeniem!");
  }
  if (error.response.status === 401) {
    logout();
    throw new Error("Twój dostęp wygasł. Zaloguj się ponownie.");
  }
  throw error;
};

/** POST without alert; rejects on error. Use with formatAxiosErrorMessage in catch. */
export const postOrThrow = async (endpoint: string, data?: unknown) => {
  authHeader();
  try {
    const response = await Axios.post(`${API}/${endpoint}`, data);
    return handleResponse(response);
  } catch (err) {
    const error = err as AxiosError<{ message?: unknown }>;
    return rejectAfterAuthHandling(error);
  }
};

/** PUT without alert; rejects on error. */
export const putOrThrow = async (endpoint: string, data?: unknown) => {
  authHeader();
  try {
    const response = await Axios.put(`${API}/${endpoint}`, data);
    return handleResponse(response);
  } catch (err) {
    const error = err as AxiosError<{ message?: unknown }>;
    return rejectAfterAuthHandling(error);
  }
};

const handleError = (error: AxiosError) => {
  if (!error.response) {
    alert("Problem z połączeniem!");
    return;
  }

  if (error.response?.status === 401) {
    // auto logout if 401 response returned from api
    alert("Twój dostęp wygasł. Zaloguj się ponownie.");
    logout();
  } else if (error.response?.status === 422) {
    alert("Niepoprawne dane!");
  } else {
    alert("Błąd sprawdź konsolę");
    console.log(error.response);
  }
};

export const get = async (endpoint: string, params: any = []) => {
  authHeader();

  return await Axios.get(`${API}/${endpoint}`, {
    params,
  })
    .then(handleResponse)
    .catch(handleError);
};

export const post = async (endpoint: string, data?: any) => {
  authHeader();

  return await Axios.post(`${API}/${endpoint}`, data)
    .then(handleResponse)
    .catch(handleError);
};

export const put = async (endpoint: string, data?: any) => {
  authHeader();

  return await Axios.put(`${API}/${endpoint}`, data)
    .then(handleResponse)
    .catch(handleError);
};

export const patch = async (endpoint: string, data?: any) => {
  authHeader();

  return await Axios.patch(`${API}/${endpoint}`, data)
    .then(handleResponse)
    .catch(handleError);
};

export const deleteReq = async (endpoint: string, data?: any) => {
  authHeader();

  return await Axios.delete(`${API}/${endpoint}`, data)
    .then(handleResponse)
    .catch(handleError);
};

/** POST a single File as multipart/form-data under the field name "file". Throws on error. */
export const uploadFile = async (endpoint: string, file: File) => {
  authHeader();
  const form = new FormData();
  form.append("file", file);
  try {
    const response = await Axios.post(`${API}/${endpoint}`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return handleResponse(response);
  } catch (err) {
    const error = err as AxiosError<{ message?: unknown }>;
    return rejectAfterAuthHandling(error);
  }
};

export const setAuthToken = (token: string) => {
  Axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
};

export const authHeader = () => {
  let user = getUserFromLocalStorage();

  if (user && user.token) setAuthToken(user.token);
};
